// Shared v2 Risk Scoring Engine
// All 7 assessment modules import from here — never duplicate this logic inline.

// ── Field normalizers (each module uses its own prefixed field names) ──────────

export const getRiskSeverity = (r) => {
  const val =
    r.userRating || r.aiUserRating || r.enterpriseUserRating ||
    r.darkWebUserRating || r.ransomwareUserRating || r.tprmUserRating ||
    r.residualRiskRating || r.aiResidualRiskRating || r.businessUnitResidualRiskRating ||
    r.enterpriseResidualRiskRating || r.darkWebResidualRiskRating ||
    r.ransomwareResidualRiskRating || r.tprmResidualRiskRating ||
    r.aiInherentRiskRating || r.risk_severity || 'LOW';
  return val.toUpperCase();
};

export const getRiskCategory = (r) =>
  r.riskCategory || r.aiRiskCategory || r.enterpriseRiskCategory || 'Uncategorized';

export const getRiskId = (r) =>
  r.riskId ?? r.aiRiskId ?? r.businessUnitRiskId ?? r.enterpriseRiskId ??
  r.darkWebRiskId ?? r.ransomwareRiskId ?? r.tprmRiskId ?? r.id;

// ── Severity-anchored posture score ───────────────────────────────────────────
// The single source of truth for every 0–100 "risk score" surfaced to users (residual risk, its
// domain breakdown, dashboard/tile scores, and the backend stage rollup mirror this exactly). It
// replaces the old mean-severity average, which diluted a lone critical toward zero as low-severity
// risks were added (1 CRITICAL = 100 but 1 CRITICAL + 99 LOW = 26 — "more risks = safer").
//
// The worst active severity present sets a band anchor; concentration within that tier adds a
// saturating bump toward the next band's floor. The result is MONOTONE — adding any unmitigated
// risk never lowers the score — and a single critical always reads >= 80 (matches the risk team's
// rule that critical exposure can't be understated). Bands: LOW 20–45, MEDIUM 45–70, HIGH 70–80,
// CRITICAL 80–100. NOTE: intentionally NOT linear, so a count-weighted average of sub-group scores
// (e.g. domains) no longer reconciles to the flat overall — callers must show the overall directly.
// Hard ceiling applied to every posture score — a maxed-out posture reads 97, never a full 100.
// MUST stay equal to the backend MonetaryImpactEngine.MaxRiskScore or server/client scores diverge.
export const MAX_RISK_SCORE = 97;
const POSTURE_ANCHOR = { CRITICAL: 80, HIGH: 70, MEDIUM: 45, LOW: 20 };
const POSTURE_CAP    = { CRITICAL: 100, HIGH: 80, MEDIUM: 70, LOW: 45 }; // next-higher band floor
const POSTURE_BUMP_K = 4; // higher = gentler climb above the anchor
const POSTURE_TIERS  = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// counts: { CRITICAL, HIGH, MEDIUM, LOW } of ACTIVE risks. Returns { score, tier, anchor, count, bump }.
export const severityPostureBreakdown = (counts) => {
  const tier = POSTURE_TIERS.find(t => (counts[t] || 0) > 0);
  if (!tier) return { score: 0, tier: null, anchor: 0, count: 0, bump: 0 };
  const anchor = POSTURE_ANCHOR[tier];
  const cap    = POSTURE_CAP[tier];
  const count  = counts[tier];
  const bump   = (cap - anchor) * (1 - Math.exp(-(count - 1) / POSTURE_BUMP_K));
  const score  = Math.round(Math.min(MAX_RISK_SCORE, anchor + bump) * 10) / 10;
  return { score, tier, anchor, count, bump: Math.round(bump * 10) / 10 };
};

export const severityPostureScore = (counts) => severityPostureBreakdown(counts).score;

// Risk-level badge for a posture score: the band the score falls in (matches the anchors above).
export const severityPostureLevel = (score) =>
  score >= 80 ? 'CRITICAL' : score >= 70 ? 'HIGH' : score >= 45 ? 'MEDIUM' : 'LOW';

// Tally ACTIVE risks by severity tier from a risk array (uses the shared severity normalizer).
export const activeSeverityCounts = (risks) => {
  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  (Array.isArray(risks) ? risks : []).forEach(r => {
    if (r.isRemediationCompleted) return;
    const sev = getRiskSeverity(r);
    if (Object.prototype.hasOwnProperty.call(counts, sev)) counts[sev]++;
  });
  return counts;
};

// ── Main analytics computation ─────────────────────────────────────────────────
// analyticsRisks : from the module's analytics endpoint (has isRemediationCompleted)
// risksData      : from the module's main risks endpoint (has accurate severity/category)
// scoring        : from the module's scoring endpoint (has CE, totalApplicableResponses)
//                  Pass null when the endpoint doesn't exist yet — engine degrades gracefully.
export const computeOrganizationAnalytics = (analyticsRisks, risksData, scoring) => {
  const arr      = Array.isArray(analyticsRisks) ? analyticsRisks : [];
  const activeArr = arr.filter(r => !r.isRemediationCompleted);
  const remediatedCount = arr.length - activeArr.length;

  // Severity counts for the Vulnerability Distribution card (active risks only)
  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  activeArr.forEach(r => {
    const sev = getRiskSeverity(r);
    if (Object.prototype.hasOwnProperty.call(counts, sev)) counts[sev]++;
  });

  // Merge risksData into analyticsRisks (both active AND remediated — remediated risks still count
  // toward each group's denominator) so the scoring pass gets accurate severity/category. Only
  // overrides with a risksData field when it's actually non-empty — some modules' risksData source
  // (e.g. BusinessUnit's question-derived RiskDetailsDto.RiskCategory) can legitimately resolve blank
  // for a given risk while analyticsRisks' own category field is populated; a blind spread would let
  // that blank value clobber the good one and everything falls back to "Uncategorized".
  const riskDetailMap = {};
  (Array.isArray(risksData) ? risksData : []).forEach(r => {
    const key = getRiskId(r);
    if (key != null) riskDetailMap[key] = r;
  });
  const scoringRisks = arr.map(r => {
    const key    = getRiskId(r);
    const detail = riskDetailMap[key] ?? {};
    const merged = { ...r };
    Object.entries(detail).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') merged[k] = v;
    });
    return merged;
  });

  const totalQuestions = scoring?.totalApplicableResponses ?? Math.max(arr.length, 1);

  // Control Effectiveness: raw Yes/Partially/No answer score, plus a remediation credit that scales
  // with how much of the identified risk backlog has been fixed — NOT a flat capped bonus (the old
  // `min(10, remediated/questions*100)` meant CE could never rise above rawCE+10 no matter how much
  // was remediated, even at 100% remediation). Scaling the credit against the REMAINING gap to 100%
  // means CE == rawCE exactly when nothing's been remediated (matches the visible Yes/Partially/No
  // breakdown with no unexplained inflation) and converges to 100% as remediatedCount -> totalRisks.
  const rawCe = scoring?.controlEffectiveness ?? 0;
  const ceRemediatedCount = scoring?.remediatedCount ?? 0;
  const ceTotalRisks = arr.length;
  const remediationRatio = ceTotalRisks > 0 ? Math.min(1, ceRemediatedCount / ceTotalRisks) : 0;
  const remediationCredit = remediationRatio * (100 - rawCe);
  const ce = rawCe + remediationCredit;

  // How CE itself was calculated (Control Effectiveness card's toggle): from the assessment's own
  // Yes/Partially/No answers, plus the proportional remediation credit above.
  const ce_breakdown = scoring ? {
    yesCount:       scoring.yesCount ?? 0,
    partiallyCount: scoring.partiallyCount ?? 0,
    noCount:        scoring.noCount ?? 0,
    totalApplicableResponses: scoring.totalApplicableResponses ?? 0,
    rawCe:          Math.round(rawCe * 10) / 10,
    remediatedCount: ceRemediatedCount,
    totalRisks:     ceTotalRisks,
    remediationRatio: Math.round(remediationRatio * 1000) / 1000,
    remediationCredit: Math.round(remediationCredit * 10) / 10,
    ce:             Math.round(ce * 10) / 10,
  } : null;

  // Overall severity counts (from merged scoring risks, active only — for the breakdown display)
  const activeScoringRisks = scoringRisks.filter(r => !r.isRemediationCompleted);
  const sevCounts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  activeScoringRisks.forEach(r => {
    const sev = getRiskSeverity(r);
    if (Object.prototype.hasOwnProperty.call(sevCounts, sev)) sevCounts[sev]++;
  });

  // Residual risk = severity-anchored posture score over the ACTIVE risk severity mix. Control
  // Effectiveness is deliberately NOT applied here (it double-counted controls already baked into
  // each risk's residual rating, and was the source of the "overall reads lower than every stage"
  // gap). CE remains its own standalone card via control_effectiveness / ce_breakdown below.
  const posture = severityPostureBreakdown(sevCounts);
  const rrs     = posture.score;

  const score_breakdown = {
    critCount: sevCounts.CRITICAL, highCount: sevCounts.HIGH,
    medCount:  sevCounts.MEDIUM,   lowCount:  sevCounts.LOW,
    totalRiskCount: scoringRisks.length,
    activeRiskCount: activeScoringRisks.length,
    tier:   posture.tier,    // worst active severity that set the anchor
    anchor: posture.anchor,  // band floor for that tier
    bumpCount: posture.count, // number of active risks at that tier
    bump:   posture.bump,    // points added above the anchor for concentration
  };

  // Per-domain posture scores — same anchored formula as the overall. NOTE: the anchored model is
  // non-linear, so these no longer average to residual_risk; consumers must show residual_risk as
  // the overall directly rather than averaging the domains.
  const domainGroups = {};
  scoringRisks.forEach(r => {
    const domain = getRiskCategory(r);
    if (!domainGroups[domain]) domainGroups[domain] = [];
    domainGroups[domain].push(r);
  });
  const domain_scores = {};
  Object.entries(domainGroups).forEach(([domain, dRisks]) => {
    domain_scores[domain] = severityPostureScore(activeSeverityCounts(dRisks));
  });

  // Risk Density: same Partially=50%/No=100% weighting as Control Effectiveness's raw answer score
  // (so a fully-open assessment's density is exactly 100 − rawCe, matching how CE treats a "Partially"
  // answer as half-effective rather than a full gap), scaled by how much of that exposure remains open
  // (active_risks ÷ total_risks) so density still drops as risks get remediated even though the
  // underlying Yes/Partially/No answers don't change when a risk is fixed.
  const hasAnswerCounts = scoring && typeof scoring.partiallyCount === 'number' && typeof scoring.noCount === 'number';
  const remediationFactor = arr.length > 0 ? activeArr.length / arr.length : 1;
  const rawAnswerDensity = hasAnswerCounts && totalQuestions > 0
    ? ((scoring.partiallyCount * 0.5 + scoring.noCount) / totalQuestions) * 100
    : (totalQuestions > 0 ? (activeArr.length / totalQuestions) * 100 : 0);
  const risk_density = rawAnswerDensity * (hasAnswerCounts ? remediationFactor : 1);

  const density_breakdown = hasAnswerCounts ? {
    partiallyCount: scoring.partiallyCount,
    noCount:        scoring.noCount,
    totalApplicableResponses: totalQuestions,
    partiallyWeight: Math.round(scoring.partiallyCount * 0.5 * 10) / 10,
    noWeight:       scoring.noCount,
    rawDensity:     Math.round(rawAnswerDensity * 10) / 10,
    activeRisks:    activeArr.length,
    totalRisks:     arr.length,
    remediationFactor: Math.round(remediationFactor * 1000) / 1000,
  } : null;

  return {
    total_risks:               arr.length,
    active_risks:              activeArr.length,
    remediated_count:          remediatedCount,
    risk_distribution:         counts,
    control_effectiveness:     Math.round(ce * 10) / 10,
    residual_risk:             Math.round(Math.max(0, Math.min(100, rrs)) * 10) / 10,
    risk_density:              Math.round(Math.min(100, risk_density) * 10) / 10,
    normalized_severity:       0,
    total_applicable_responses: totalQuestions,
    scoring_version:           scoring?.scoringVersion ?? 'v2',
    domain_scores,
    score_breakdown,
    ce_breakdown,
    density_breakdown,
  };
};
